import { BusinessPartnerCompanyAttributes } from '@albatrosdeveloper/ave-models-package';
import BusinessPartnerAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/business-partners/business-partner/business-partner.entity';
/**
 * Minimal scope shape needed to resolve a bpCompanies[] entry. It is a plain object rather
 * than a service so it can be built from anywhere, including plain classes such as export
 * processors that don't go through Angular DI.
 */
export interface BpCompanyScopeContext {
  seesAll: boolean;
  allowedCompanyIds: string[];
  /**
   * When set, {@link resolveBpCompanyEntry} resolves the `bpCompanies[]` entry
   * for THIS exact company — used by exports that let the user pick ONE
   * company up front (e.g. the main "Socios de Negocio" export's required
   * company selector) instead of "first entry within the user's whole allowed
   * scope". Takes precedence over the `seesAll`/`allowedCompanyIds` rules.
   */
  selectedCompanyId?: string | null;
}

/**
 * Resolves the `bpCompanies[]` entry a list/export column should read for a given business
 * partner, now that group/subgroup/creditLimit/routeArea/isTaxExempt live per-company instead
 * of on the deprecated BP root fields (the v2 editor — `business-partner-multicompany-editor`
 * — only writes `bpCompanies[]`).
 *
 * Resolution rule (see docs/patterns/multi-company-filtering.md):
 *  1. `bpCompanies` empty (legacy BP never migrated) → `undefined`. Callers fall back to the
 *     deprecated root field in that case.
 *  2. User scoped to exactly ONE company → that company's entry, if present.
 *  3. Otherwise (scoped to several companies) → the first entry whose `companyMongoId` is
 *     within the user's scope.
 *  4. See-all user with no match (rare — see-all has no restriction to match against) → the
 *     FIRST `bpCompanies` entry — deterministic, never left blank.
 *  5. Scoped user (single or multi-company) with NO entry in their scope → `undefined`, NOT
 *     `bpCompanies[0]`. The list query scopes on the DEPRECATED root `companies[]` field, not
 *     on `bpCompanies[].companyMongoId` (see the `bpCompanies[] does NOT mirror the deprecated
 *     root fields 1:1` gotcha in the doc) — those two arrays can disagree, so a scoped user
 *     reaching this function is NOT guaranteed to have an in-scope `bpCompanies` entry.
 *     Falling back to index 0 would risk showing another company's data; callers already
 *     fall back to the deprecated root field (or a `??`/`—` placeholder) when this returns
 *     `undefined`.
 */
export function resolveBpCompanyEntry(
  bp: Pick<BusinessPartnerAttributes, 'bpCompanies'> | null | undefined,
  context: BpCompanyScopeContext
): BusinessPartnerCompanyAttributes | undefined {
  const companies = bp?.bpCompanies;
  if (!companies?.length) return undefined;

  // A specific company was chosen for this export (e.g. the main BP list's
  // company selector) — resolve THAT company's entry regardless of the user's
  // wider allowed scope. No match (BP has no bpCompanies entry for the chosen
  // company) → undefined, same as any other unresolved case; callers already
  // fall back to the deprecated root field.
  if (context.selectedCompanyId) {
    return companies.find(
      (c) => c?.companyMongoId?.toString() === context.selectedCompanyId
    );
  }

  if (!context.seesAll && context.allowedCompanyIds.length) {
    const allowedSet = new Set(context.allowedCompanyIds.map(String));
    const inScope = companies.find((c) => allowedSet.has(c?.companyMongoId?.toString() ?? ''));
    // Scoped user, nothing in their scope: return undefined rather than an arbitrary
    // (possibly out-of-scope) entry — see rule 5 above.
    return inScope;
  }

  return companies[0];
}
